import { NavLink, useNavigate } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { THEME_LABELS_TH, THEME_MODES, saveThemeMode, type ThemeMode } from '../lib/theme';
import { useQueue } from '../hooks/useQueue';
import { useOnlineStatus } from '../hooks/useSession';
import { api, type SessionResponse } from '../lib/api';
import { ROLE_LABELS_TH } from '../../shared/types';

interface Props {
  session: SessionResponse;
  mode: ThemeMode;
  onModeChange: (mode: ThemeMode) => void;
  children: ReactNode;
}

export function AppShell({ session, mode, onModeChange, children }: Props) {
  const online = useOnlineStatus();
  const { outstanding, counts, retry } = useQueue();
  const [menuOpen, setMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const navigate = useNavigate();

  /**
   * ออกจากระบบแล้วโหลดหน้าใหม่ทั้งหน้า
   *
   * ตั้งใจใช้ location.reload แทนการเปลี่ยน state ใน React
   * เพื่อล้างข้อมูลที่ค้างอยู่ในหน่วยความจำของแอปให้หมด
   * ไม่ให้ข้อมูลของคนก่อนหน้าค้างอยู่ตอนคนถัดไปล็อกอินบนเครื่องเดียวกัน
   */
  const signOut = async () => {
    setSigningOut(true);
    try {
      await api.logout();
    } catch {
      // ถึง server จะตอบไม่สำเร็จ ก็ยังต้องพาผู้ใช้ออกจากหน้าจอนี้
    }
    window.location.href = '/';
  };

  // ปิดเมนูเมื่อเปลี่ยนหน้า ไม่งั้นเมนูจะค้างทับเนื้อหาใหม่
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener('popstate', close);
    return () => window.removeEventListener('popstate', close);
  }, [menuOpen]);

  const isSupervisor = session.user.role !== 'field_officer';
  const isAdmin = session.user.role === 'administrator';

  return (
    <div className="app">
      <header className="appbar">
        <div className="appbar-inner">
          <button
            type="button"
            className="btn-quiet"
            aria-expanded={menuOpen}
            aria-controls="main-nav"
            onClick={() => setMenuOpen((open) => !open)}
          >
            เมนู
          </button>
          <span className="appbar-title grow">MeterPoint</span>

          {outstanding > 0 && (
            <button
              type="button"
              className="pill pill-warn"
              onClick={() => navigate('/my')}
              style={{ cursor: 'pointer' }}
            >
              คิว {outstanding}
            </button>
          )}
          {!online && (
            <span className="pill pill-bad" role="status">
              ออฟไลน์
            </span>
          )}
        </div>

        {menuOpen && (
          <nav id="main-nav" className="wrap stack" style={{ paddingBottom: 'var(--space-4)' }}>
            <div className="stack" style={{ gap: 'var(--space-1)' }}>
              <NavItem to="/capture" onNavigate={() => setMenuOpen(false)}>
                บันทึกรายการใหม่
              </NavItem>
              <NavItem to="/my" onNavigate={() => setMenuOpen(false)}>
                รายการของฉัน
              </NavItem>
              {isSupervisor && (
                <>
                  <NavItem to="/search" onNavigate={() => setMenuOpen(false)}>
                    ค้นหาทุกรายการ
                  </NavItem>
                  <NavItem to="/dashboard" onNavigate={() => setMenuOpen(false)}>
                    แดชบอร์ด
                  </NavItem>
                </>
              )}
              {isAdmin && (
                <>
                  <NavItem to="/admin/users" onNavigate={() => setMenuOpen(false)}>
                    จัดการผู้ใช้
                  </NavItem>
                  <NavItem to="/admin" onNavigate={() => setMenuOpen(false)}>
                    ตั้งค่าระบบ
                  </NavItem>
                </>
              )}
            </div>

            <div className="field">
              <span className="label" id="theme-label">
                โหมดการแสดงผล
              </span>
              <div className="segmented" role="group" aria-labelledby="theme-label">
                {THEME_MODES.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={mode === option}
                    onClick={() => {
                      onModeChange(option);
                      void saveThemeMode(option);
                    }}
                  >
                    {THEME_LABELS_TH[option]}
                  </button>
                ))}
              </div>
            </div>

            <div className="stack-sm">
              <p className="small muted">
                {session.user.displayName} · {ROLE_LABELS_TH[session.user.role]}
                {session.user.areaCode ? ` · เขต ${session.user.areaCode}` : ''}
              </p>
              <p className="small muted">{session.user.email}</p>

              <button
                type="button"
                className="btn btn-secondary btn-block"
                onClick={() => navigate('/account/password')}
              >
                เปลี่ยนรหัสผ่าน
              </button>

              <button
                type="button"
                className="btn btn-secondary btn-block"
                disabled={signingOut}
                onClick={() => void signOut()}
              >
                {signingOut ? 'กำลังออกจากระบบ…' : 'ออกจากระบบ'}
              </button>
            </div>
          </nav>
        )}
      </header>

      {counts.failed > 0 && (
        <div className="wrap" style={{ paddingTop: 'var(--space-3)' }}>
          <div className="banner banner-bad" role="alert">
            <div className="grow">
              <b>มี {counts.failed} รายการส่งไม่สำเร็จ</b>
              ข้อมูลยังอยู่ครบในเครื่อง อย่าล้างข้อมูลเบราว์เซอร์จนกว่าจะส่งสำเร็จ
            </div>
            <button type="button" className="btn btn-secondary" onClick={() => retry()}>
              ส่งใหม่
            </button>
          </div>
        </div>
      )}

      <main className="main">{children}</main>
    </div>
  );
}

function NavItem({
  to,
  children,
  onNavigate,
}: {
  to: string;
  children: ReactNode;
  onNavigate: () => void;
}) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) => (isActive ? 'btn btn-secondary' : 'btn btn-quiet')}
      style={{ justifyContent: 'flex-start' }}
    >
      {children}
    </NavLink>
  );
}
